/**
 * Iter-110 — Collaborators (Delivery)
 *
 * Lists every collaborator (filtered by role + status) with a quick search box.
 * Clicking a row navigates to /collaborators/:user_id which lists every mission
 * they're assigned to. Visible to CEO, GESTIONNAIRE, and MANAGER.
 *
 * For MANAGER, missions where they are NOT the manager_id show up as a "name only"
 * row (no expand / no detail link). CEO and GESTIONNAIRE see every mission with
 * full detail link.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Search, ChevronRight, User as UserIcon } from "lucide-react";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/lib/auth";

export default function Collaborators() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    api.get("/users").then((r) => setUsers(r.data || [])).catch(() => setUsers([]));
  }, []);

  const visible = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return (users || [])
      .filter((u) => u.status === "ACTIVE")
      .filter((u) => !q || (u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q)))
      .sort((a, b) => (a.name || a.email || "").localeCompare(b.name || b.email || ""));
  }, [users, filter]);

  const isAdminScope = me.role === "CEO" || me.role === "GESTIONNAIRE";

  return (
    <div className="p-4 md:p-8 lg:p-12 max-w-[1100px]" data-testid="collaborators-page">
      <div className="text-[11px] uppercase tracking-[0.25em] text-brand-navy-500 mb-3 font-mono">Delivery</div>
      <h1 className="font-heading text-4xl font-bold tracking-tight mb-6">Collaborators</h1>

      <div className="bg-white border border-brand-navy/15 rounded-lg p-4 mb-5">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Search by name or email…"
            data-testid="collaborators-filter"
            className="pl-9 h-10"
          />
        </div>
      </div>

      <div className="bg-white border border-brand-navy/15 rounded-lg overflow-hidden">
        <div className="grid grid-cols-12 px-4 py-2.5 text-[10px] uppercase tracking-widest text-zinc-500 font-mono bg-zinc-50 border-b border-zinc-200">
          <div className="col-span-5">Name</div>
          <div className="col-span-3">Role</div>
          <div className="col-span-3">Email</div>
          <div className="col-span-1" />
        </div>
        {visible.length === 0 && (
          <div className="px-5 py-12 text-center text-sm text-zinc-500" data-testid="collaborators-empty">
            No active collaborator{filter ? " matches your search" : ""}.
          </div>
        )}
        {visible.map((u) => (
          <Link
            key={u.user_id}
            to={`/collaborators/${u.user_id}`}
            data-testid={`collaborator-row-${u.user_id}`}
            className="grid grid-cols-12 items-center px-4 py-3 border-b border-zinc-100 last:border-b-0 hover:bg-brand-cream-50 transition-colors"
          >
            <div className="col-span-5 flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-brand-navy text-white inline-flex items-center justify-center text-xs font-semibold shrink-0">
                {(u.name || u.email || "?").trim().charAt(0).toUpperCase()}
              </div>
              <div>
                <div className="text-sm font-medium text-brand-ink">{u.name || "—"}</div>
                {u.is_freelance && <div className="text-[10px] uppercase tracking-wider text-violet-700 font-mono">Freelance</div>}
              </div>
            </div>
            <div className="col-span-3 text-xs font-mono text-zinc-700">{u.role}</div>
            <div className="col-span-3 text-xs text-zinc-600 truncate">{u.email}</div>
            <div className="col-span-1 flex justify-end">
              <ChevronRight size={14} className="text-zinc-400" />
            </div>
          </Link>
        ))}
      </div>

      {!isAdminScope && (
        <p className="mt-4 text-xs text-zinc-500" data-testid="collab-manager-disclaimer">
          As a manager, you can expand only the missions where you are the manager. Other missions are listed by name only.
        </p>
      )}
    </div>
  );
}
